import React, { useContext, useState } from 'react';
import classes from './CsvImporter.module.scss';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { useCSVReader } from 'react-papaparse';
import classNames from 'classnames';
import { Loader } from '../../../components/containers/Loader';
import { CsvImportedPost, CvsReaderType, ImportResults, ValidationErrorDetails } from '../../../models/csv-importer';
import { generateCSVTemplate } from '../../../helpers/csvImporterHelpers';
import { Table } from '../../../components/containers/Table';
import { CsvColumnNames } from '../../../constants/csv-importer';
import { CheckboxWithAction } from '../../../components/actions/CheckboxWithAction';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { PlanImporterType } from 'models/graphql';
import { BiggerScreenPlaceholder } from '../../../components/containers/BiggerScreenPlaceHolder';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { BoxNotification } from 'components/containers/BoxNotification';

export type CsvImporterViewProps = {
  zoneHover: boolean;
  setZoneHover: React.Dispatch<React.SetStateAction<boolean>>;
  processing: boolean;
  setProcessing: (processing: boolean) => void;
  validateFile: (file: File) => void;
  validationHandler: (results: ImportResults) => void;
  errors: string[];
  validationErrorDetails: ValidationErrorDetails;
  posts: CsvImportedPost[];
  loading: boolean;
};

type CustomTableRowProps = {
  post?: CsvImportedPost;
  index: number;
  validationErrorDetails: ValidationErrorDetails;
};

export const CustomTableRow = ({ post, index, validationErrorDetails }: CustomTableRowProps): JSX.Element => {
  if (!post) return <></>;

  const errors = validationErrorDetails[index] || undefined;
  const assignClassNames = (column: CsvColumnNames, value: string | undefined): string => {
    const classNamesToUse: string[] = [];
    const hasError = errors && errors[column];

    if (hasError) {
      classNamesToUse.push(classes.ValidationError);
    }

    if (hasError && !value) {
      classNamesToUse.push(classes.EmptyColumn);
    }

    return classNamesToUse.join(' ');
  };

  return (
    <tr key={`csv-imported-post-${index + 1}`}>
      <td className={validationErrorDetails[index] ? classes.ValidationError : undefined}>{index + 1}</td>
      <td>{post.text}</td>
      <td id={`col${index}link`} className={assignClassNames(CsvColumnNames.LINK, post.link)}>
        {post.link}
      </td>
      <td id={`col${index}images`} className={assignClassNames(CsvColumnNames.IMAGES, post.images)}>
        {post.images}
      </td>
      <td id={`col${index}schedule`} className={assignClassNames(CsvColumnNames.SCHEDULED_TIME, post.scheduledTime)}>
        {post.scheduledTime}
      </td>
      <td id={`col${index}category`} className={assignClassNames(CsvColumnNames.CATEGORY, post.category)}>
        {post.category}
      </td>
      <td id={`col${index}evergreen`} className={assignClassNames(CsvColumnNames.EVERGREEN, post.evergreen)}>
        {post.evergreen}
      </td>
      <td>{post.title}</td>
      <td>{post.firstComment}</td>
    </tr>
  );
};

export const CsvImporterView = ({
  loading,
  zoneHover,
  setZoneHover,
  processing,
  setProcessing,
  validateFile,
  validationHandler,
  validationErrorDetails,
  errors,
  posts
}: CsvImporterViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const { CSVReader } = useCSVReader();
  const [showOnlyErrors, setShowOnlyErrors] = useState<boolean>(true);
  const hasDataErrors = !!Object.keys(validationErrorDetails).length;
  const { importType } = useOrganizationPlan();
  const { isTemporallySuspended } = useSubscriptionStatus();
  const isDisabled = importType === PlanImporterType.BASIC_IMPORTER || isTemporallySuspended;
  const CsvUploaderConfig = {
    maxSize: 15 * 1048576, // 15 MB in bytes
    maxFiles: 1,
    multiple: false,
    delimiter: ',',
    header: true,
    disabled: isDisabled,

  };

  const dropZoneClasses = classNames(classes.DropZone, {
    [classes.ZoneHover]: zoneHover
  });

  const importerButtonClasses = classNames(classes.ImporterButton, {
    [classes.ImporterButtonDisabled]: zoneHover || isDisabled
  });

  const breakpoint = useBreakpoints();
  if (breakpoint === 'small') {
    return <BiggerScreenPlaceholder />;
  }

  const handleClickContactUs = () => {
    beacon('open');
    beacon('navigate', '/ask');
  };

  return (
    <CenteredContentLayout headerTitle={'Bulk import from a spreadsheet'} loading={loading} hasSidebar={false}>
      {importType === PlanImporterType.BASIC_IMPORTER && <AdvancedFeature feature={AdvancedFeatures.CSV} />}
      {isTemporallySuspended && <div className='margin-bottom-small-20'><BoxNotification type="error">
                <>
                  <span>To protect against spam, publishing from this Organization is temporarily locked.</span>
                  <br />
                  <span>
                    Please <a onClick={handleClickContactUs}>contact us</a> to unlock your account.
                  </span>
                </>
              </BoxNotification></div>}
      <CSVReader
        {...CsvUploaderConfig}
        validator={(file: File) => {
          setProcessing(true);
          validateFile(file);
        }}
        onUploadAccepted={(results: ImportResults) => {
          setProcessing(true);
          validationHandler(results);
        }}
        onUploadRejected={() => setProcessing(false)}
        onDragOver={(event: DragEvent) => {
          event.preventDefault();
          setZoneHover(true);
        }}
        onDragLeave={(event: DragEvent) => {
          event.preventDefault();
          setZoneHover(false);
        }}
        onDrop={(event: React.DragEvent) => {
          event.preventDefault();
          setZoneHover(false);
        }}>
        {({ getRootProps }: CvsReaderType) => (
          <div {...getRootProps()} className={dropZoneClasses}>
            {processing ? (
              <Loader variation={'dark'} size={'large'} />
            ) : (
              <div className={'grid-y full-height'} style={{ textAlign: 'center' }}>
                <div className={'align-center'} style={{ margin: 'auto 0' }}>
                  <div>Drag-and-drop your CSV file here<br /><br /></div>
                  <div>
                    <a
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        beacon('article', '712', { type: 'modal' });
                      }}>
                      Learn more
                    </a>{' '}
                    or{' '}
                    <a
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        generateCSVTemplate();
                      }}>
                      Download a CSV Template
                    </a>
                  </div>
                  <div className={'margin-top-small-16'}>
                    <div className={importerButtonClasses}>Upload CSV File</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CSVReader>
      <div style={{ paddingBottom: '32px' }}>
        {errors.length > 0 && (
          <div className={classes.ErrorContainer}>
            {!hasDataErrors ? (
              <div className={classes.FileErrorBox}>
                {errors.map((e, index) => (
                  <div key={`file-error-${index}`}>{e}</div>
                ))}
              </div>
            ) : (
              <div className={classes.ErrorBox}>
                <div>
                  Sorry, some of the rows in your CSV file have invalid content. Please edit your CSV file and upload
                  again:
                </div>
                <ul className={classes.ErrorList}>
                  {errors.map((e, index) => (
                    <li key={`error-${index}`}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            {hasDataErrors && (
              <>
                <div className={classes.HideValidRowsCheckbox}>
                  <div className={'grid-x grid-padding-x align-justify'}>
                    <div className={'cell auto'}>
                      <CheckboxWithAction
                        name={'showErrors'}
                        size={'large'}
                        onClickHandler={() => setShowOnlyErrors(!showOnlyErrors)}
                        checked={!showOnlyErrors}
                        label={showOnlyErrors ? 'Show all rows' : 'Showing all rows'}
                      />
                    </div>
                    <div className={'cell shrink'}>
                      {!showOnlyErrors ? posts.length : Object.keys(validationErrorDetails).length} rows
                    </div>
                  </div>
                </div>
                <div className={classes.TableContainer}>
                  <Table tableStyle={'stacked_scrolling'}>
                    <>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Text</th>
                          <th>Link</th>
                          <th>Image</th>
                          <th>Scheduled Time</th>
                          <th>Category</th>
                          <th>Evergreen</th>
                          <th>Title</th>
                          <th>First Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posts.map(
                          (post, index) =>
                            post &&
                            !(!validationErrorDetails[index] && showOnlyErrors) && (
                              <CustomTableRow key={index} {...{ post, index, validationErrorDetails }} />
                            )
                        )}
                      </tbody>
                    </>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </CenteredContentLayout>
  );
};
